import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { AvatarState, Option } from '@rivethealth/data/options';
import { Permission } from '@rivethealth/data/user-account';
import { sortBy } from 'lodash';
import { SavedUser, UserService } from '../api/user.service';
import { useAccountId } from '../app/account/account.state';
import { AnchoredPosition } from './callout/callout-position';
import { HookDirective } from './core/hook';
import { useEffect, useLoad, useState } from './core/hooks';
import { RouteService } from './state/route.service';

@Component({
  selector: 'assignee-select-dropdown',
  templateUrl: './assignee-select-dropdown.component.html',
  styleUrls: ['./assignee-select-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssigneeSelectDropdownComponent extends HookDirective {
  constructor(
    private readonly routeService: RouteService,
    private readonly userService: UserService,
    _cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
  }

  @ViewChild('dropdown', { static: false })
  readonly dropdownRef: ElementRef<HTMLElement>;

  @Input() userPermission: Permission | null = null;

  @Input() userAvatars: { user: SavedUser; avatarState: AvatarState }[];

  @Output() assigneeChange = new EventEmitter<string>();

  get _() {
    const accountId = useAccountId(this.routeService);

    const [menuOpen, setMenuOpen] = useState<boolean | undefined>(undefined);

    useEffect(() => {
      if (menuOpen !== undefined) {
        this.dropdownRef.nativeElement.focus();
      }
    }, [menuOpen]);

    const [usersLoadable] = useLoad(
      () =>
        this.userService.getUsers(
          accountId,
          false,
          this.userPermission ? [this.userPermission] : [],
        ),
      [accountId, this.userPermission],
    );

    const users = usersLoadable.data || [];

    const userOptions = [
      { id: 'unassign', title: 'Unassign', subtitle: null },
      ...sortBy(users, user => user.name.toLocaleLowerCase()).map(
        user =>
          <Option>{
            id: user.id.toString(),
            title: user.name,
            subtitle: null,
            avatarState: this.userAvatars.find(
              avatar => avatar.user.id === user.id,
            )?.avatarState,
          },
      ),
    ];

    const [selectedUserId, _setSelectedUserId] = useState<string | null>(null);

    const selectedUserName = userOptions.find(
      o => o.id === selectedUserId,
    )?.title;

    const setSelectedUserId = (userId: string) => {
      _setSelectedUserId(userId);
      this.assigneeChange.emit(userId);
      setMenuOpen(false);
    };

    return {
      accountId,
      menuOpen,
      setMenuOpen,
      userOptions,
      selectedUserId,
      setSelectedUserId,
      selectedUserName,
    };
  }

  readonly AnchoredPosition = AnchoredPosition;
}
